import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../components/PageConfigProvider/constants';
import type {
  PrimaryFilterConfig,
  TabContent,
} from '../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../components/PageConfigProvider/types';
import { FilterId } from '../filters/types/filters';

const geographyCompositionSnapshotItems: TabContent['items'] = [
  {
    position: { x: 0, width: 8, y: 0, height: 8 },
    id: 'geography-composition-snapshot-headcount',
    title: 'Headcount',
    info: {
      text: 'Our estimate of the total workforce. These counts include subsidiaries and may include both formal employees and contingent workers, depending on your preference settings. More information on the methodologies used for this metric can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#sampling-weights',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['headcount'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 8, width: 8, y: 0, height: 8 },
    id: 'geography-composition-snapshot-growth-rate',
    title: 'Growth Rate',
    info: {
      text: 'The growth rate is the percent change in the total workforce. It is the difference between the hiring rate and attrition rate.',
    },
    plot: {
      defaultMetricOptions: ['growth_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 16, width: 8, y: 0, height: 8 },
    id: 'geography-composition-snapshot-hiring-rate',
    title: 'Hiring Rate',
    info: {
      text: 'The annual rate at which workers join, relative to the total headcount. It is the 12-month moving sum of inflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['hiring_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 24, width: 8, y: 0, height: 8 },
    id: 'geography-composition-snapshot-attrition-rate',
    title: 'Attrition Rate',
    info: {
      text: 'The annual rate at which workers leave, relative to the total headcount. It is the 12-month moving sum of outflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['attrition_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 32, width: 8, y: 0, height: 8 },
    id: 'geography-composition-snapshot-tenure',
    title: 'Tenure',
    info: {
      text: 'The average amount of time that workers have spent in their position.',
    },
    plot: {
      defaultMetricOptions: ['tenure'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 40, width: 8, y: 0, height: 8 },
    id: 'geography-composition-snapshot-salary',
    title: 'Salary',
    info: {
      text: 'Our estimate of average salary. More information on the salary model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['salary'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 0, width: 16, y: 8, height: 14 },
    id: 'geography-composition-snapshot-role',
    title: 'Role',
    info: {
      text: 'The composition of workers by occupation. More information on the job representation and taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      secondaryDimension: 'role',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
    },
  },
  {
    position: { x: 16, width: 16, y: 8, height: 14 },
    id: 'geography-composition-snapshot-industry',
    title: 'Industry',
    info: {
      text: 'The composition of workers by industry.',
    },
    plot: {
      secondaryDimension: 'rics',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
    },
  },
  {
    position: { x: 32, width: 16, y: 8, height: 14 },
    id: 'geography-composition-snapshot-skill',
    title: 'Skill',
    info: {
      text: 'The composition of workers by skill. More information on the skill model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#combining-skill-clusters',
        },
      ],
    },
    plot: {
      secondaryDimension: 'skills',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
      hideOther: true,
    },
  },
  {
    position: { x: 0, width: 16, y: 22, height: 14 },
    id: 'geography-composition-snapshot-gender',
    title: 'Gender',
    info: {
      text: 'The composition of workers by gender, predicted using first name. More information on the gender model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'gender',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
    },
  },
  {
    position: { x: 16, width: 16, y: 22, height: 14 },
    id: 'geography-composition-snapshot-ethnicity',
    title: 'Ethnicity',
    info: {
      text: 'The composition of workers by ethnicity, predicted using first name, last name, and geography. More information on the ethnicity model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'ethnicity',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
    },
  },
  {
    position: { x: 32, width: 16, y: 22, height: 14 },
    id: 'geography-composition-snapshot-education',
    title: 'Education',
    info: {
      text: 'The composition of workers by the highest educational degree.',
    },
    plot: {
      secondaryDimension: 'education',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
    },
  },
];

const geographyCompositionOvertimeItems: TabContent['items'] = [
  {
    position: { x: 0, width: 8, y: 0, height: 8 },
    id: 'geography-composition-overtime-headcount',
    title: 'Headcount',
    info: {
      text: 'Our estimate of the total workforce. These counts include subsidiaries and may include both formal employees and contingent workers, depending on your preference settings. More information on the methodologies used for this metric can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#sampling-weights',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['headcount'],
      chartType: 'line',
    },
  },
  {
    position: { x: 8, width: 8, y: 0, height: 8 },
    id: 'geography-composition-overtime-growth-rate',
    title: 'Growth Rate',
    info: {
      text: 'The growth rate is the percent change in the total workforce. It is the difference between the hiring rate and attrition rate.',
    },
    plot: {
      defaultMetricOptions: ['growth_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 16, width: 8, y: 0, height: 8 },
    id: 'geography-composition-overtime-hiring-rate',
    title: 'Hiring Rate',
    info: {
      text: 'The annual rate at which workers join, relative to the total headcount. It is the 12-month moving sum of inflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['hiring_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 24, width: 8, y: 0, height: 8 },
    id: 'geography-composition-overtime-attrition-rate',
    title: 'Attrition Rate',
    info: {
      text: 'The annual rate at which workers leave, relative to the total headcount. It is the 12-month moving sum of outflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['attrition_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 32, width: 8, y: 0, height: 8 },
    id: 'geography-composition-overtime-tenure',
    title: 'Tenure',
    info: {
      text: 'The average amount of time that workers have spent in their position.',
    },
    plot: {
      defaultMetricOptions: ['tenure'],
      chartType: 'line',
    },
  },
  {
    position: { x: 40, width: 8, y: 0, height: 8 },
    id: 'geography-composition-overtime-salary',
    title: 'Salary',
    info: {
      text: 'Our estimate of average salary. More information on the salary model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#salary',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['salary'],
      chartType: 'line',
    },
  },
  {
    position: { x: 0, width: 16, y: 8, height: 14 },
    id: 'geography-composition-overtime-role',
    title: 'Role',
    info: {
      text: 'The composition of workers by occupation. More information on the job representation and taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      secondaryDimension: 'role',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 16, width: 16, y: 8, height: 14 },
    id: 'geography-composition-overtime-industry',
    title: 'Industry',
    info: {
      text: 'The composition of workers by industry.',
    },
    plot: {
      secondaryDimension: 'rics',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 32, width: 16, y: 8, height: 14 },
    id: 'geography-composition-overtime-skill',
    title: 'Skill',
    info: {
      text: 'The composition of workers by skill. More information on the skill model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#combining-skill-clusters',
        },
      ],
    },
    plot: {
      secondaryDimension: 'skills',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 0, width: 16, y: 22, height: 14 },
    id: 'geography-composition-overtime-gender',
    title: 'Gender',
    info: {
      text: 'The composition of workers by gender, predicted using first name. More information on the gender model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'gender',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 16, width: 16, y: 22, height: 14 },
    id: 'geography-composition-overtime-ethnicity',
    title: 'Ethnicity',
    info: {
      text: 'The composition of workers by ethnicity, predicted using first name, last name, and geography. More information on the ethnicity model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#gender-and-ethnicity',
        },
      ],
    },
    plot: {
      secondaryDimension: 'ethnicity',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 32, width: 16, y: 22, height: 14 },
    id: 'geography-composition-overtime-education',
    title: 'Education',
    info: {
      text: 'The composition of workers by the highest educational degree.',
    },
    plot: {
      secondaryDimension: 'education',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
];

const sharedConfig: Pick<TabContent, 'grid'> & {
  primaryFilter: PrimaryFilterConfig;
} = {
  primaryFilter: {
    id: FilterId.GEOGRAPHY,
    selectionLimit: 6,
    selectionDefault: 5,
  },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
};

const geographyCompositionSnapshot: TabContent = {
  id: 'geography-composition-snapshot',
  items: geographyCompositionSnapshotItems,
  ...sharedConfig,
  secondaryFilters: [
    FilterId.SNAPSHOT_DATE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.COMPANY,
    FilterId.ROLE,
    FilterId.SKILL,
    FilterId.CUSTOM_SKILL,
    FilterId.SENIORITY,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.DATA_ADJUSTMENTS,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
};

const geographyCompositionOvertime: TabContent = {
  id: 'geography-composition-overtime',
  items: geographyCompositionOvertimeItems,
  ...sharedConfig,
  secondaryFilters: [
    FilterId.DATE_RANGE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.COMPANY,
    FilterId.ROLE,
    FilterId.SKILL,
    FilterId.CUSTOM_SKILL,
    FilterId.SENIORITY,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.DATA_ADJUSTMENTS,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
  primaryFilter: {
    ...sharedConfig.primaryFilter,
    tagColors: true,
  },
};

export const geographyComposition: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.GEOGRAPHY_COMPOSITION,
  revelioPageId: RevelioPageId.GEOGRAPHY_COMPOSITION,
  name: 'Compositions',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'geography-composition-snapshot',
      label: 'Snapshot',
      tabContent: geographyCompositionSnapshot,
      isActive: true,
    },
    {
      id: 'geography-composition-overtime',
      label: 'Over Time',
      tabContent: geographyCompositionOvertime,
      isActive: false,
    },
  ],
};
